import React, { useState } from 'react';
import { FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import { sendSubmission, buildMailtoLink } from '../utils/mailer';

// Shared service-request form used on the Contact page and in the popup modal.
const ServiceRequestForm = ({ source = 'Service Request', onSuccess }) => {
  const [form, setForm] = useState({ fullName: '', email: '', company: '', service: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [mode, setMode] = useState('sent');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setError('');

    const fields = {
      Name: form.fullName,
      Email: form.email,
      Company: form.company,
      'Service Interested In': form.service || 'Not specified',
      Message: form.message,
    };

    setSending(true);
    try {
      await sendSubmission({ source, fields, replyTo: form.email });
      setMode('sent');
      setSubmitted(true);
      setForm({ fullName: '', email: '', company: '', service: '', message: '' });
      if (onSuccess) onSuccess();
    } catch (err) {
      // Backend unavailable — fall back to the visitor's own mail client.
      window.location.href = buildMailtoLink(
        `Service Request — ${form.company || form.fullName || 'Website Inquiry'}`,
        [
          `Name: ${form.fullName}`,
          `Email: ${form.email}`,
          `Company: ${form.company}`,
          `Service Interested In: ${form.service}`,
          '',
          'Message:',
          form.message,
        ]
      );
      setMode('mailto');
      setSubmitted(true);
      setForm({ fullName: '', email: '', company: '', service: '', message: '' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <span className="contact-block-tag">Service Request</span>
      <h2 className="contact-form-title">Tell Us About Your Project.</h2>
      <p className="contact-form-desc">
        Fill out the form below and one of our specialists will reach out within one business day.
      </p>

      {submitted && (
        <div className="contact-success">
          <FaCheckCircle className="contact-success-icon" />
          <div>
            {mode === 'sent' ? (
              <>
                <strong>Thanks — your request is with our team.</strong>
                <div>We've received it and will reply within one business day.</div>
              </>
            ) : (
              <>
                <strong>Your email app should now open with your message ready to go.</strong>
                <div>Hit send there to reach us — we respond within one business day.</div>
              </>
            )}
          </div>
        </div>
      )}

      <form className="contact-form" onSubmit={handleSubmit}>
        <div className="contact-field-row">
          <div className="contact-field">
            <label htmlFor="fullName">Full Name *</label>
            <input type="text" id="fullName" name="fullName" value={form.fullName} onChange={handleChange} required placeholder="Your full name" />
          </div>
          <div className="contact-field">
            <label htmlFor="email">Business Email *</label>
            <input type="email" id="email" name="email" value={form.email} onChange={handleChange} required placeholder="you@company.com" />
          </div>
        </div>

        <div className="contact-field-row">
          <div className="contact-field">
            <label htmlFor="company">Company *</label>
            <input type="text" id="company" name="company" value={form.company} onChange={handleChange} required placeholder="Your company name" />
          </div>
          <div className="contact-field">
            <label htmlFor="service">Service Interested In *</label>
            <select id="service" name="service" value={form.service} onChange={handleChange} required>
              <option value="">Select a service area...</option>
              <option value="cybersecurity">Cybersecurity Services</option>
              <option value="managed-it">Managed IT Services</option>
              <option value="development">Software Development</option>
              <option value="other">Something else</option>
            </select>
          </div>
        </div>

        <div className="contact-field">
          <label htmlFor="message">Message / Requirements *</label>
          <textarea id="message" name="message" value={form.message} onChange={handleChange} required rows="5" placeholder="Tell us about your project, timeline, and any specific requirements..." />
        </div>

        <button type="submit" className="contact-submit-btn" disabled={sending}>
          {sending ? 'Sending…' : <>Send Inquiry <FaArrowRight /></>}
        </button>
        {error && <p className="contact-error-note" role="alert">{error}</p>}
      </form>
    </>
  );
};

export default ServiceRequestForm;
